import { useEffect, useRef, useState } from "react";
import BookingModal from "./BookingModal";

interface FinalCTAProps {
  onRequestLeads: () => void;
}

export default function FinalCTA({ onRequestLeads }: FinalCTAProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const content = entry.target.querySelector(".cta-content") as HTMLElement;
            if (content) {
              content.style.opacity = "1";
              content.style.transform = "translateY(0)";
            }
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section id="book-call" className="relative py-28 md:py-40 overflow-hidden" ref={sectionRef}>
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/bd4e385dac217ae0c05287d4140be42f.jpg"
            alt="Solar panels background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/80 to-[#0A1F0F]/90"></div>
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8 text-center">
          <div
            className="cta-content"
            style={{ opacity: 0, transform: "translateY(32px)", transition: "opacity 0.7s ease, transform 0.7s ease" }}
          >
            <div className="inline-flex items-center gap-2 bg-green-500/20 border border-green-500/30 rounded-full px-4 py-1.5 mb-8">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0"></span>
              <span className="text-green-300 text-sm font-medium">Limited availability per region</span>
            </div>

            <h2 className="text-4xl md:text-6xl font-bold text-white leading-tight tracking-tight mb-5">
              Start Receiving Solar Leads{" "}
              <span className="text-green-400">This Week</span>
            </h2>

            <p className="text-gray-300 text-lg md:text-xl mb-10 max-w-xl mx-auto leading-relaxed">
              Limited availability per region — secure your spot now and start closing more solar deals.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <button
                onClick={onRequestLeads}
                className="bg-green-500 hover:bg-green-400 text-black font-bold px-10 py-4 rounded-full text-base transition-all duration-200 active:scale-95 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                Request Sample Leads
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
              <button
                onClick={() => setBookingOpen(true)}
                className="bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-10 py-4 rounded-full text-base transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                Book a Call
              </button>
            </div>

            <div className="flex flex-wrap justify-center gap-6 text-gray-400 text-sm">
              {[
                { icon: "ri-lock-line", label: "No Contracts" },
                { icon: "ri-test-tube-line", label: "Test Leads First" },
                { icon: "ri-time-line", label: "Setup in 24hrs" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${item.icon} text-green-400 text-sm`}></i>
                  </div>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </>
  );
}